import { Transform, Type } from 'class-transformer';
import {
  ArrayMaxSize,
  ArrayNotEmpty,
  IsArray,
  IsBoolean,
  IsDate,
  IsEmail,
  IsEnum,
  IsIn,
  IsInt,
  IsNotEmpty,
  IsNumber,
  IsOptional,
  IsString,
  Length,
  Max,
  MaxLength,
  Min,
  ValidateNested,
} from 'class-validator';

export enum AttributionChannelDto {
  CODE = 'CODE',
  LINK = 'LINK',
  CONSULTANT = 'CONSULTANT',
  MANUAL = 'MANUAL',
}

export enum CommissionSchemaDto {
  ACTIVITY = 'ACTIVITY',
  CONSULTANT = 'CONSULTANT',
}

class PartnerCommissionOverridesDto {
  @IsOptional()
  @IsNumber()
  @Min(0)
  @Max(100)
  firstYearPctActivity?: number | null;

  @IsOptional()
  @IsNumber()
  @Min(0)
  @Max(100)
  renewalPctActivity?: number | null;

  @IsOptional()
  @IsNumber()
  @Min(0)
  @Max(100)
  firstYearPctConsultant?: number | null;

  @IsOptional()
  @IsNumber()
  @Min(0)
  @Max(100)
  renewalPctConsultant?: number | null;

  @IsOptional()
  @IsString()
  notes?: string | null;
}

export class PartnerProfileDto extends PartnerCommissionOverridesDto {
  @IsString()
  @IsNotEmpty()
  id!: string;

  @IsString()
  @IsNotEmpty()
  tenantId!: string;

  @IsBoolean()
  isActive!: boolean;

  /**
   * Sezione commissioni dell'area partner visibile al partner (KPI, grafico
   * trimestri, provvigioni per-cliente). Default false: si abilita per profilo
   * dal back office — non tutti i partner lavorano con le commissioni.
   */
  @IsBoolean()
  commissionsVisible!: boolean;

  /**
   * Assistente IA per i consulenti designati del partner (studio questionari e
   * scadenzario), con quota giornaliera condivisa sullo studio. Acceso di
   * default; il back office lo spegne per partner.
   */
  @IsBoolean()
  consultantAiEnabled!: boolean;

  @IsOptional()
  @IsDate()
  activatedAt?: Date | null;

  @IsString()
  @IsNotEmpty()
  activatedById!: string;

  @IsOptional()
  @IsDate()
  deactivatedAt?: Date | null;
}

export class PartnerListItemDto extends PartnerProfileDto {
  @IsString()
  @IsNotEmpty()
  tenantName!: string;

  @IsInt()
  clientiAttributedCount!: number;

  @IsOptional()
  @IsString()
  discountCode!: string | null;

  @IsOptional()
  @IsString()
  signupLinkUrl!: string | null;

  @IsOptional()
  @IsNumber()
  discountPercentOff!: number | null;

  /**
   * Consulenti designati salvati sul profilo. Popolato solo dall'endpoint di
   * lista: la checklist di configurazione lo usa per ripristinare la
   * selezione invece di ricontrollare tutti i candidati a ogni apertura.
   */
  @IsOptional()
  @IsArray()
  @IsString({ each: true })
  consultantUserIds?: string[];
}

export class CreatePartnerDto extends PartnerCommissionOverridesDto {
  @IsString()
  @IsNotEmpty()
  tenantId!: string;

  /**
   * Consulenti designati del partner: devono essere membri attivi di ruolo
   * Consulente del tenant del partner. La designazione NON cambia i ruoli;
   * un id non idoneo (non membro, inattivo o altro ruolo) è rifiutato con 422.
   */
  @IsOptional()
  @IsArray()
  @IsString({ each: true })
  consultantUserIds?: string[];

  @IsOptional()
  @IsInt()
  @Min(0)
  @Max(100)
  clientDiscountPercent?: number;

  /** Assente = acceso (default del profilo). */
  @IsOptional()
  @IsBoolean()
  consultantAiEnabled?: boolean;
}

/**
 * Creazione one-shot di un'azienda partner "consulente-only" dal back office:
 * crea utente Clerk (email + password generata), tenant nuovo (ruolo
 * Consulente, subscription ACTIVE, onboarding completato, nome = companyName)
 * e profilo PartnerProfile attivo con asset (discount code + signup link).
 */
export class CreatePartnerCompanyDto extends PartnerCommissionOverridesDto {
  @IsString()
  @IsNotEmpty()
  @Length(1, 120)
  companyName!: string;

  /**
   * Email dell'account owner dell'azienda. Normalizzata lato server (trim +
   * lowercase) per allinearsi alla chiave email usata da utenti e inviti.
   */
  @IsEmail()
  @IsNotEmpty()
  @Transform(({ value }) => (typeof value === 'string' ? value.trim().toLowerCase() : value))
  email!: string;

  @IsOptional()
  @IsInt()
  @Min(0)
  @Max(100)
  clientDiscountPercent?: number;
}

/**
 * Conversione di un tenant ACTIVITY esistente in azienda partner consulente:
 * gli Owner attivi diventano Consulente (stesso utente, stessa email), il
 * tenant diventa CONSULTANT e riceve profilo partner + comp access. Nessun
 * consultantUserIds: i designati sono gli ex-Owner per costruzione.
 */
export class ConvertOwnerToPartnerDto extends PartnerCommissionOverridesDto {
  @IsString()
  @IsNotEmpty()
  tenantId!: string;

  @IsOptional()
  @IsInt()
  @Min(0)
  @Max(100)
  clientDiscountPercent?: number;

  /** Assente = acceso (default del profilo). Ignorato se il partner è già attivo. */
  @IsOptional()
  @IsBoolean()
  consultantAiEnabled?: boolean;
}

export class UpdatePartnerDto extends PartnerCommissionOverridesDto {
  /**
   * Consulenti designati del partner: devono essere membri attivi di ruolo
   * Consulente del tenant del partner. La designazione NON cambia i ruoli;
   * un id non idoneo (non membro, inattivo o altro ruolo) è rifiutato con 422.
   */
  @IsOptional()
  @IsArray()
  @IsString({ each: true })
  consultantUserIds?: string[];

  /**
   * Visibilità della sezione commissioni per il partner (default false).
   * Solo su update: create/convert non la accettano, il nuovo profilo parte
   * nascosto e l'admin la abilita dopo dall'attivazione.
   */
  @IsOptional()
  @IsBoolean()
  commissionsVisible?: boolean;

  /** Riaccenderlo riusa (o crea) lo studio del partner. */
  @IsOptional()
  @IsBoolean()
  consultantAiEnabled?: boolean;
}

export class InvitePartnerConsultantDto {
  @IsString()
  @IsNotEmpty()
  tenantId!: string;

  /**
   * Normalizzata lato server (trim + lowercase) per allinearsi alla chiave
   * email usata da utenti e inviti team.
   */
  @IsEmail()
  @IsNotEmpty()
  @Transform(({ value }) => (typeof value === 'string' ? value.trim().toLowerCase() : value))
  email!: string;
}

export class ManualAttributionDto {
  @IsString()
  @IsNotEmpty()
  tenantId!: string;

  @IsString()
  @IsNotEmpty()
  reason!: string;

  @IsOptional()
  @Type(() => Date)
  @IsDate()
  effectiveFrom?: Date;

  /**
   * Consulente che segue il cliente. Facoltativo: un partner puo' portare un
   * cliente senza consulente. Quando valorizzato deve essere un membro attivo
   * di ruolo Consulente del tenant del partner (422 altrimenti) e il cliente
   * entra anche nel perimetro consulente (/consultant/clients e le
   * statistiche), che l'attribuzione partner da sola non tocca.
   */
  @IsOptional()
  @IsString()
  @IsNotEmpty()
  viaConsultantUserId?: string;
}

/**
 * Modalita' di rimozione di un'attribuzione (decisione: due modalita', mai una
 * cancellazione secca). END = rapporto finito, le provvigioni maturate
 * restano. VOID = attribuzione errata, esce anche dallo storico del rendiconto.
 */
export enum AttributionRemovalModeDto {
  END = 'END',
  VOID = 'VOID',
}

export class RemoveAttributionDto {
  @IsEnum(AttributionRemovalModeDto)
  mode!: AttributionRemovalModeDto;

  @IsString()
  @Length(10, 2000)
  reason!: string;
}

export class PartnerReportRowDto {
  @IsString()
  @IsNotEmpty()
  tenantId!: string;

  @IsString()
  tenantName!: string;

  @IsEnum(AttributionChannelDto)
  channel!: AttributionChannelDto;

  @IsEnum(CommissionSchemaDto)
  commissionSchema!: CommissionSchemaDto;

  @IsInt()
  periodIndex!: number;

  @IsInt()
  invoiceCount!: number;

  @IsInt()
  amountNetCents!: number;

  @IsInt()
  refundedCents!: number;

  @IsInt()
  commissionCents!: number;

  /**
   * Aliquota percentuale applicata alla riga (art. 5.1: il rendiconto mostra
   * aliquota e provvigione per vendita). 0 se la riga non è provvigionabile
   * (fuori dai primi 36 mesi o prima dell'attribuzione).
   */
  @IsOptional()
  @IsNumber()
  @Min(0)
  @Max(100)
  ratePct?: number;

  @Type(() => Date)
  @IsDate()
  periodStart!: Date;
}

/** Stato del rendiconto trimestrale pre-generato (snapshot notturno). */
export enum PartnerRendicontoStatusDto {
  READY = 'READY',
  FAILED = 'FAILED',
}

/** Una riga della lista dei rendiconti trimestrali pre-generati di un partner. */
export class PartnerRendicontoListItemDto {
  @IsString()
  quarter!: string;

  @IsEnum(PartnerRendicontoStatusDto)
  status!: PartnerRendicontoStatusDto;

  @IsInt()
  totalCommissionCents!: number;

  @IsInt()
  rowCount!: number;

  @Type(() => Date)
  @IsDate()
  generatedAt!: Date;

  @IsOptional()
  @Type(() => Date)
  @IsDate()
  regeneratedAt?: Date | null;

  @IsOptional()
  @IsString()
  error?: string | null;

  /** I totali live divergono dallo snapshot: è consigliata la rigenerazione. */
  @IsBoolean()
  drift!: boolean;
}

/** Columns the admin partner list may be sorted by (server-side, whole dataset). */
export const PARTNER_SORT_FIELDS = ['tenantName', 'isActive', 'activatedAt'] as const;
export type PartnerSortBy = (typeof PARTNER_SORT_FIELDS)[number];

export const SORT_ORDERS = ['asc', 'desc'] as const;
export type SortOrder = (typeof SORT_ORDERS)[number];

export class ListPartnersQueryDto {
  @IsOptional()
  @IsString()
  @MaxLength(200)
  search?: string;

  @IsOptional()
  @IsString()
  tenantId?: string;

  @IsOptional()
  @Transform(({ value }) => (value === 'true' ? true : value === 'false' ? false : value))
  @IsBoolean()
  isActive?: boolean;

  @IsOptional()
  @IsIn(PARTNER_SORT_FIELDS)
  sortBy?: PartnerSortBy;

  @IsOptional()
  @IsIn(SORT_ORDERS)
  sortOrder?: SortOrder;

  @IsOptional()
  @Type(() => Number)
  @IsInt()
  @Min(1)
  page?: number;

  @IsOptional()
  @Type(() => Number)
  @IsInt()
  @Min(1)
  @Max(100)
  pageSize?: number;
}

export class PartnerStatusCountDto {
  @IsBoolean()
  isActive!: boolean;

  @IsInt()
  count!: number;
}

/**
 * Unica azione bulk supportata oggi: la disattivazione ha effetti collaterali
 * reali (revoca signup link/coupon, revoca comp access Stripe) applicati per
 * id dal service esistente — mai un `updateMany` grezzo. Non estendere questo
 * enum con azioni che richiederebbero dati non presenti in una selezione
 * multipla (es. "activate" richiede la configurazione provvigioni per singolo
 * tenant, non è un semplice flag).
 */
export const BULK_PARTNER_ACTIONS = ['deactivate'] as const;
export type BulkPartnerAction = (typeof BULK_PARTNER_ACTIONS)[number];

export class BulkPartnerActionDto {
  @IsArray()
  @ArrayNotEmpty()
  @ArrayMaxSize(200)
  @IsString({ each: true })
  ids!: string[];

  @IsIn(BULK_PARTNER_ACTIONS)
  action!: BulkPartnerAction;
}

export class BulkPartnerResultDto {
  @IsInt()
  affected!: number;

  @IsInt()
  skipped!: number;
}

/** Lifecycle status of the partner's referral signup link (see SignupLinkStatus on the backend). */
export type ConsultantPartnerLinkStatus = 'active' | 'expired' | 'revoked' | 'full';

export class ConsultantPartnerReferralDto {
  /** `${FRONTEND_URL}/signup-link/${token}`; null when the partner has no unrevoked link. */
  @IsOptional()
  @IsString()
  linkUrl!: string | null;

  @IsOptional()
  @IsString()
  token!: string | null;

  @IsOptional()
  @IsIn(['active', 'expired', 'revoked', 'full'])
  status!: ConsultantPartnerLinkStatus | null;

  @IsOptional()
  @IsInt()
  @Min(0)
  trialDays!: number | null;

  @IsOptional()
  @IsInt()
  @Min(0)
  @Max(100)
  discountPercentOff!: number | null;

  /** Registrations completed through the referral link. */
  @IsInt()
  @Min(0)
  usedCount!: number;

  @IsOptional()
  @IsInt()
  @Min(0)
  remaining!: number | null;

  @IsOptional()
  @Type(() => Date)
  @IsDate()
  expiresAt!: Date | null;
}

/**
 * Da dove arriva un cliente del partner. SELF_ACQUIRED: link, codice,
 * attribuzione via consulente o azienda gestita dai suoi consulenti.
 * PLATFORM_ASSIGNED: attribuzione manuale del back office o collegamento del
 * back office senza attribuzione.
 */
export enum ClientOriginDto {
  SELF_ACQUIRED = 'SELF_ACQUIRED',
  PLATFORM_ASSIGNED = 'PLATFORM_ASSIGNED',
}

/** Stato commerciale del cliente: paga (Stripe ACTIVE/PAST_DUE), in prova, senza abbonamento. */
export enum ClientCommercialStateDto {
  PAYING = 'PAYING',
  TRIAL = 'TRIAL',
  NO_SUBSCRIPTION = 'NO_SUBSCRIPTION',
}

/**
 * Traccia che ha deciso l'origine del cliente. LINK/CODE/CONSULTANT/MANUAL
 * sono i canali dell'attribuzione attiva; LINK_SIGNUP = iscritto dal link del
 * partner senza attribuzione (nessun incasso ancora); MANAGED = azienda
 * gestita da un consulente designato; LINKED_ONLY = collegato dal back office
 * a un consulente designato, senza attribuzione.
 */
export enum ClientOriginChannelDto {
  LINK = 'LINK',
  CODE = 'CODE',
  CONSULTANT = 'CONSULTANT',
  LINK_SIGNUP = 'LINK_SIGNUP',
  MANAGED = 'MANAGED',
  MANUAL = 'MANUAL',
  LINKED_ONLY = 'LINKED_ONLY',
}

/** Conteggi per stato commerciale: paying = PAYING, trial = TRIAL, noSubscription = NO_SUBSCRIPTION. */
export class ConsultantPartnerStateCountsDto {
  @IsInt()
  @Min(0)
  paying!: number;

  @IsInt()
  @Min(0)
  trial!: number;

  @IsInt()
  @Min(0)
  noSubscription!: number;
}

export class ConsultantPartnerOriginChannelCountDto {
  @IsEnum(ClientOriginChannelDto)
  channel!: ClientOriginChannelDto;

  @IsInt()
  @Min(0)
  count!: number;
}

export class ConsultantPartnerOriginSummaryDto {
  @IsEnum(ClientOriginDto)
  origin!: ClientOriginDto;

  @IsInt()
  @Min(0)
  total!: number;

  @ValidateNested()
  @Type(() => ConsultantPartnerStateCountsDto)
  byState!: ConsultantPartnerStateCountsDto;

  /** Paganti con il pagamento in ritardo (Stripe PAST_DUE): sono gia' dentro `byState.paying`. */
  @IsInt()
  @Min(0)
  pastDue!: number;

  /** Tutti i canali dell'origine, in ordine fisso, anche a zero. */
  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => ConsultantPartnerOriginChannelCountDto)
  byChannel!: ConsultantPartnerOriginChannelCountDto[];
}

/** selfAcquired = SELF_ACQUIRED, platformAssigned = PLATFORM_ASSIGNED. */
export class ConsultantPartnerMonthByOriginDto {
  @ValidateNested()
  @Type(() => ConsultantPartnerStateCountsDto)
  selfAcquired!: ConsultantPartnerStateCountsDto;

  @ValidateNested()
  @Type(() => ConsultantPartnerStateCountsDto)
  platformAssigned!: ConsultantPartnerStateCountsDto;
}

export class ConsultantPartnerMonthCountDto {
  /**
   * 'YYYY-MM' (Europe/Rome) del momento in cui il cliente e' arrivato al
   * partner: decidedAt dell'attribuzione, creazione del tenant per iscritti
   * dal link e aziende gestite, collegamento del back office per i collegati.
   */
  @IsString()
  month!: string;

  @ValidateNested()
  @Type(() => ConsultantPartnerMonthByOriginDto)
  byOrigin!: ConsultantPartnerMonthByOriginDto;
}

export class ConsultantPartnerQuarterTotalDto {
  /** 'YYYY-Qn' (UTC) of the invoiced period start. */
  @IsString()
  quarter!: string;

  @IsInt()
  @Min(0)
  cents!: number;
}

export class ConsultantPartnerStatsDto {
  /**
   * Clienti attuali del partner divisi per origine (sempre entrambe, prima
   * SELF_ACQUIRED). Un tenant e' contato una volta sola; esclusi tenant
   * disattivati, anonimizzati e studi dei consulenti.
   */
  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => ConsultantPartnerOriginSummaryDto)
  clientiPerOrigine!: ConsultantPartnerOriginSummaryDto[];

  /** Clienti per mese di arrivo, per origine e stato commerciale (ordine crescente). */
  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => ConsultantPartnerMonthCountDto)
  clientiPerMese!: ConsultantPartnerMonthCountDto[];

  /** Zero quando le provvigioni non sono attive per il partner. */
  @IsInt()
  @Min(0)
  commissioniTotaliCents!: number;

  commissioniPerTrimestre!: ConsultantPartnerQuarterTotalDto[];

  /**
   * Clienti in prova che porteranno provvigione al primo incasso
   * (attribuiti al partner o iscritti dal suo link): senza questo numero un
   * totale provvigioni a zero sembra un errore. Zero con provvigioni non attive.
   */
  @IsInt()
  @Min(0)
  clientiInProvaTotal!: number;

  /** Fine prova piu' vicina tra quei clienti = primo incasso atteso. */
  @IsOptional()
  @Type(() => Date)
  @IsDate()
  prossimoIncassoAttesoAt!: Date | null;
}

/**
 * Dashboard "Partner" per il consulente di un tenant partner: referral link
 * (senza il codice sconto raw, stessa regola della landing pubblica), banner
 * input e statistiche aggregate. Solo aggregati: le righe per-tenant del
 * rendiconto restano un'esclusiva platform-admin.
 */
export class ConsultantPartnerResponseDto {
  @IsString()
  @IsNotEmpty()
  tenantName!: string;

  @IsOptional()
  @Type(() => Date)
  @IsDate()
  activatedAt!: Date | null;

  /**
   * Provvigioni attive per il partner (`PartnerProfile.commissionsVisible`,
   * impostazione di back office). Falso: la dashboard non parla di provvigioni
   * da nessuna parte.
   */
  @IsBoolean()
  commissionsEnabled!: boolean;

  referral!: ConsultantPartnerReferralDto;

  stats!: ConsultantPartnerStatsDto;
}

/**
 * Un tenant attribuito a un partner, come mostrato nella sezione "clienti
 * attribuiti" del back office. Include anche le attribuzioni superate
 * (`supersededAt` valorizzato): sono lo storico dei passaggi tra partner e
 * l'admin deve poterle distinguere, non perderle.
 */
export class PartnerClientDto {
  /** Id della riga TenantAttribution: serve per targettare l'azione di rimozione. */
  @IsString()
  @IsNotEmpty()
  attributionId!: string;

  @IsString()
  @IsNotEmpty()
  tenantId!: string;

  @IsString()
  tenantName!: string;

  @IsEnum(AttributionChannelDto)
  channel!: AttributionChannelDto;

  @IsEnum(CommissionSchemaDto)
  commissionSchema!: CommissionSchemaDto;

  @IsOptional()
  @IsString()
  viaConsultantUserId!: string | null;

  /** Nome del consulente che segue il cliente, null quando l'attribuzione non ne indica uno. */
  @IsOptional()
  @IsString()
  viaConsultantName!: string | null;

  @IsString()
  subscriptionStatus!: string;

  @IsString()
  planTier!: string;

  @Type(() => Date)
  @IsDate()
  decidedAt!: Date;

  @Type(() => Date)
  @IsDate()
  effectiveFrom!: Date;

  @IsOptional()
  @Type(() => Date)
  @IsDate()
  supersededAt!: Date | null;

  /** Valorizzato quando l'attribuzione e' stata annullata (rimozione VOID): esce dal rendiconto ma resta nello storico. */
  @IsOptional()
  @Type(() => Date)
  @IsDate()
  voidedAt!: Date | null;

  @IsOptional()
  @IsString()
  removalReason!: string | null;

  @Type(() => Date)
  @IsDate()
  tenantCreatedAt!: Date;
}

export class PartnerClientListDto {
  items!: PartnerClientDto[];

  /** Righe che soddisfano il filtro `status` richiesto: e' il totale da paginare. */
  @IsInt()
  total!: number;

  /** Attribuzioni ancora in corso, indipendenti dal filtro: alimenta il contatore della tab. */
  @IsInt()
  activeCount!: number;

  /** Attribuzioni superate o annullate, indipendenti dal filtro: alimenta il contatore della tab. */
  @IsInt()
  historyCount!: number;

  @IsInt()
  page!: number;

  @IsInt()
  pageSize!: number;
}

/**
 * Attive (in corso), storico (superate o annullate) o entrambe. Lo storico sta
 * in una tab separata nel back office: mescolarlo alle attive rendeva il
 * conteggio della sezione incoerente con quello del profilo partner.
 */
export type PartnerClientsStatusFilter = 'active' | 'history' | 'all';

export class ListPartnerClientsQueryDto {
  @IsOptional()
  @Type(() => Number)
  @IsInt()
  @Min(1)
  page?: number;

  @IsOptional()
  @Type(() => Number)
  @IsInt()
  @Min(1)
  @Max(100)
  pageSize?: number;

  /** Le stringhe di query arrivano come 'true' | 'false': normalizza prima di @IsBoolean(). */
  @IsOptional()
  @Transform(({ value }) => (value === 'true' ? true : value === 'false' ? false : value))
  @IsBoolean()
  includeSuperseded?: boolean;

  /**
   * Filtro di stato della tab. Ha la precedenza su `includeSuperseded`, che
   * resta per i consumer esistenti. Default `all`.
   */
  @IsOptional()
  @IsIn(['active', 'history', 'all'])
  status?: PartnerClientsStatusFilter;
}

/**
 * Una riga del dettaglio provvigioni di un cliente: la fattura incassata con
 * prezzo di listino e sconto, l'imponibile su cui il motore calcola, e la
 * provvigione risultante. Le righe con `isRefund` sono storni: importi e
 * provvigione sono negativi e spiegano perche' il totale non e' la somma dei
 * rinnovi.
 */
export class ConsultantClientCommissionRowDto {
  @IsString()
  @IsNotEmpty()
  invoiceRef!: string;

  @Type(() => Date)
  @IsDate()
  periodStart!: Date;

  @IsInt()
  amountSubtotalCents!: number;

  @IsInt()
  amountDiscountCents!: number;

  @IsOptional()
  @IsInt()
  discountPercentOff!: number | null;

  @IsOptional()
  @IsString()
  discountCode!: string | null;

  @IsInt()
  amountNetCents!: number;

  @IsInt()
  refundedCents!: number;

  @IsNumber()
  ratePct!: number;

  @IsInt()
  periodIndex!: number;

  @IsInt()
  commissionCents!: number;

  @IsBoolean()
  isRefund!: boolean;
}

export class ConsultantClientCommissionTotalsDto {
  @IsInt()
  grossCents!: number;

  @IsInt()
  discountCents!: number;

  @IsInt()
  netCents!: number;

  @IsInt()
  refundedCents!: number;

  @IsInt()
  commissionCents!: number;
}

/** Totali sempre su TUTTE le righe, mai sulla pagina corrente. */
export class ConsultantClientCommissionResponseDto {
  @IsEnum(CommissionSchemaDto)
  commissionSchema!: CommissionSchemaDto;

  @IsNumber()
  firstYearPct!: number;

  @IsNumber()
  renewalPct!: number;

  @ValidateNested()
  @Type(() => ConsultantClientCommissionTotalsDto)
  totals!: ConsultantClientCommissionTotalsDto;

  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => ConsultantClientCommissionRowDto)
  rows!: ConsultantClientCommissionRowDto[];

  @IsInt()
  total!: number;
}
